<template>
  <el-dialog
    :title="isCreate ? '新增节点' : `编辑节点 #${editNode.id}`"
    :visible.sync="dialogVisible"
    width="90%"
    top="2vh"
    :modal="false"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <div class="node-edit-scroll" v-loading="uploading || editNode._autoCompleting">
      <!-- ==================== 固定节点 ==================== -->
      <template v-if="editNode.node_type === 'fixed'">
        <div class="fixed-node-layout">
          <!-- 左侧内容区 -->
          <div class="fixed-node-left">
            <el-form label-width="120px" :model="editNode">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="节点类型">
                    <el-select v-model="editNode.node_type" placeholder="请选择类型">
                      <el-option label="AI场景" value="ai" />
                      <el-option label="固定剧情场景" value="fixed" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="ID">
                    <el-input :value="editNode.id || '新建中...'" :disabled="true" />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="大章节号">
                    <el-select v-model="editNode.game_chapter_id" placeholder="请选择大章节" filterable clearable>
                      <el-option v-for="c in chapters" :key="c.id" :label="c.title" :value="c.id" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="标题">
                <el-input v-model="editNode.title" placeholder="请输入节点标题" />
              </el-form-item>

              <el-form-item label="剧情输入">
                <el-input
                  type="textarea"
                  v-model="editNode.dialogue_text"
                  :autosize="{ minRows: 6, maxRows: 20 }"
                  placeholder="每行格式为：角色名:对话内容"
                  class="fixed-node-textarea"
                />
                <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                  提示：每行以"角色名:"开头，后面跟对话内容。保存时会自动解析成对话列表。
                </div>
              </el-form-item>
            </el-form>
          </div>

          <!-- 右侧媒体资源区 -->
          <div class="fixed-node-right">
            <!-- 背景图 -->
            <div class="media-section bg-section">
              <el-upload
                class="bg-upload-wrapper"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :data="{ game_id: gameId }"
                :on-success="(res) => handleUploadSuccess(res, 'bg_url')"
                :show-file-list="false"
                :before-upload="() => { uploading = true; return true; }"
              >
                <div class="bg-preview-container">
                  <div class="bg-preview">
                    <img v-if="editNode.bg_url" :src="editNode.bg_url" class="bg-image" />
                    <div v-else class="bg-placeholder">
                      <i class="el-icon-picture"></i>
                      <div>背景图</div>
                    </div>
                    <div class="bg-overlay">
                      <i class="el-icon-edit"></i>
                      <div class="overlay-text">点击更换背景图</div>
                    </div>
                  </div>
                  <div class="bg-filename">
                    <span class="label">封面</span>
                    <span class="filename">{{ editNode.bg_url ? getFileName(editNode.bg_url) : '封面文件名' }}</span>
                  </div>
                </div>
              </el-upload>
            </div>

            <!-- 音乐播放器 -->
            <div class="media-section music-section">
              <div class="music-player">
                <audio v-if="editNode.music_url" :src="editNode.music_url" controls style="width: 100%; height: 40px;" />
                <div v-else class="music-placeholder">
                  <i class="el-icon-headset"></i>
                  <span>音乐播放器</span>
                </div>
              </div>
              <el-upload
                class="music-upload-wrapper"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :data="{ game_id: gameId }"
                :on-success="(res) => handleUploadSuccess(res, 'music_url')"
                :show-file-list="false"
                :before-upload="() => { uploading = true; return true; }"
              >
                <div class="music-filename">
                  <span class="label">音乐</span>
                  <span class="filename">{{ editNode.music_url ? getFileName(editNode.music_url) : '音乐文件名' }}</span>
                  <div class="music-overlay">
                    <i class="el-icon-edit"></i>
                    <span>点击更换背景音乐</span>
                  </div>
                </div>
              </el-upload>
            </div>
          </div>
        </div>
      </template>

      <!-- ==================== AI 节点 ==================== -->
      <template v-else>
        <div class="ai-node-layout">
          <!-- 左侧内容区 -->
          <div class="ai-node-left">
            <el-form label-width="120px" :model="editNode">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="节点类型">
                    <el-select v-model="editNode.node_type" placeholder="请选择类型">
                      <el-option label="AI场景" value="ai" />
                      <el-option label="固定剧情场景" value="fixed" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="ID">
                    <el-input :value="editNode.id || '新建中...'" :disabled="true" />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="大章节号">
                    <el-select v-model="editNode.game_chapter_id" placeholder="请选择大章节" filterable clearable>
                      <el-option v-for="c in chapters" :key="c.id" :label="c.title" :value="c.id" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="标题">
                <el-input v-model="editNode.title" placeholder="请输入节点标题" />
              </el-form-item>

              <el-form-item label="故事背景">
                <el-input type="textarea" v-model="editNode.story_background" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入故事背景..." />
              </el-form-item>

              <!-- 当前剧情 -->
              <el-form-item label="当前剧情" class="current-plot-item">
                <div class="current-plot-container">
                  <div class="plot-row">
                    <label class="plot-label">开始剧情</label>
                    <el-input type="textarea" v-model="editNode.current_plot_new.plot_start" :rows="3" placeholder="请输入开始时的剧情描述..." class="plot-textarea" />
                  </div>
                  <div class="plot-row">
                    <label class="plot-label">过程剧情</label>
                    <el-input type="textarea" v-model="editNode.current_plot_new.plot_process" :rows="5" placeholder="请输入进行中的剧情描述..." class="plot-textarea" />
                  </div>
                  <!-- 多结局管理 -->
                  <div class="plot-row" style="flex-direction: column; align-items: stretch;">
                    <label class="plot-label" style="width: auto; margin-bottom: 8px;">剧情结尾（多结局）</label>
                    <div v-for="(ending, eIdx) in plotEndArray" :key="'ending-' + eIdx" style="border: 1px solid #E6A23C; padding: 12px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
                      <div style="display: flex; align-items: center; margin-bottom: 8px;">
                        <el-input v-model="ending.name" placeholder="结局名称" style="width: 150px; margin-right: 10px;" size="small" />
                        <el-select v-model="ending.next_node_id" placeholder="跳转节点（可选）" clearable filterable style="width: 200px; margin-right: 10px;" size="small">
                          <el-option v-for="n in otherNodes" :key="n.id" :label="`#${n.id} ${n.title}`" :value="n.id" />
                        </el-select>
                        <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEnding(eIdx)">删除</el-button>
                      </div>
                      <el-input type="textarea" v-model="ending.content" :rows="3" placeholder="请输入结局故事内容..." style="margin-bottom: 8px;" />
                      <div style="margin-top: 8px;">
                        <span style="font-size: 12px; color: #909399;">输出示例：</span>
                        <div v-for="(ex, exIdx) in ending.output_examples" :key="'eoe-' + exIdx" style="display: flex; align-items: center; margin-top: 4px;">
                          <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                          <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEndingOutputExample(ending, exIdx)" />
                        </div>
                        <el-button type="primary" plain size="mini" @click="addEndingOutputExample(ending)" style="margin-top: 4px;">添加输出示例</el-button>
                      </div>
                    </div>
                    <el-button type="warning" plain size="mini" @click="addEnding">添加结局</el-button>
                  </div>
                </div>
              </el-form-item>

              <!-- 图片提示词 -->
              <el-form-item label="图片提示词">
                <el-input type="textarea" v-model="editNode.image_prompt" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入图片提示词..." />
              </el-form-item>
            </el-form>
          </div>

          <!-- 右侧媒体资源区 -->
          <div class="ai-node-right">
            <!-- 背景图 -->
            <div class="media-section bg-section">
              <el-upload
                class="bg-upload-wrapper"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :data="{ game_id: gameId }"
                :on-success="(res) => handleUploadSuccess(res, 'bg_url')"
                :show-file-list="false"
                :before-upload="() => { uploading = true; return true; }"
              >
                <div class="bg-preview-container">
                  <div class="bg-preview">
                    <img v-if="editNode.bg_url" :src="editNode.bg_url" class="bg-image" />
                    <div v-else class="bg-placeholder">
                      <i class="el-icon-picture"></i>
                      <div>背景图</div>
                    </div>
                    <div class="bg-overlay">
                      <i class="el-icon-edit"></i>
                      <div class="overlay-text">点击更换背景图</div>
                    </div>
                  </div>
                  <div class="bg-filename">
                    <span class="label">封面</span>
                    <span class="filename">{{ editNode.bg_url ? getFileName(editNode.bg_url) : '封面文件名' }}</span>
                  </div>
                </div>
              </el-upload>
            </div>

            <!-- 音乐播放器 -->
            <div class="media-section music-section">
              <div class="music-player">
                <audio v-if="editNode.music_url" :src="editNode.music_url" controls style="width: 100%; height: 40px;" />
                <div v-else class="music-placeholder">
                  <i class="el-icon-headset"></i>
                  <span>音乐播放器</span>
                </div>
              </div>
              <el-upload
                class="music-upload-wrapper"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :data="{ game_id: gameId }"
                :on-success="(res) => handleUploadSuccess(res, 'music_url')"
                :show-file-list="false"
                :before-upload="() => { uploading = true; return true; }"
              >
                <div class="music-filename">
                  <span class="label">音乐</span>
                  <span class="filename">{{ editNode.music_url ? getFileName(editNode.music_url) : '音乐文件名' }}</span>
                  <div class="music-overlay">
                    <i class="el-icon-edit"></i>
                    <span>点击更换背景音乐</span>
                  </div>
                </div>
              </el-upload>
            </div>
          </div>
        </div>

        <!-- 补全节点内容按钮 -->
        <div style="text-align: center; padding: 16px 24px;">
          <el-button
            type="primary"
            plain
            :loading="editNode._autoCompleting"
            :disabled="!canAutoComplete"
            @click="doAutoComplete"
            icon="el-icon-magic-stick"
          >补全节点内容</el-button>
          <div v-if="!canAutoComplete" style="color: #909399; font-size: 12px; margin-top: 4px;">
            请先填写剧情开端、过程、结尾、开场白和攻略方式
          </div>
        </div>

        <!-- AI节点设置区域 -->
        <div class="ai-node-settings">
          <el-form label-width="120px" :model="editNode">
            <el-divider content-position="center">
              <span style="color: #6366f1; font-weight: 600; font-size: 15px;">AI节点设置</span>
            </el-divider>

            <!-- 进入条件 -->
            <el-form-item label="进入条件">
              <div v-for="(cond, cIdx) in editNode.entry_conditions" :key="cIdx" style="border: 1px solid #E6A23C; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
                <el-row :gutter="10">
                  <el-col :span="6">
                    <el-select v-model="cond.type" placeholder="条件类型" style="width: 100%;">
                      <el-option label="货币" value="货币" />
                      <el-option label="属性" value="属性" />
                      <el-option label="好感度" value="好感度" />
                      <el-option label="道具" value="道具" />
                      <el-option label="服装" value="服装" />
                    </el-select>
                  </el-col>
                  <template v-if="cond.type === '货币'">
                    <el-col :span="6"><el-input-number v-model="cond.value" placeholder="数值" style="width: 100%;" /></el-col>
                    <el-col :span="6"><el-checkbox v-model="cond.is_free">是否限免</el-checkbox></el-col>
                  </template>
                  <template v-else-if="cond.type === '属性'">
                    <el-col :span="6">
                      <el-select v-model="cond.role_id" placeholder="角色" style="width: 100%;" @change="() => { cond.attribute_name = '' }">
                        <el-option v-for="role in condAttrRoles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <el-select v-model="cond.attribute_name" placeholder="属性名称" style="width: 100%;">
                        <el-option v-for="attr in condAttrNames(cond)" :key="attr" :label="attr" :value="attr" />
                      </el-select>
                    </el-col>
                    <el-col :span="4"><el-input-number v-model="cond.attribute_value" placeholder="数值要求" style="width: 100%;" /></el-col>
                  </template>
                  <template v-else-if="cond.type === '好感度'">
                    <el-col :span="6">
                      <el-select v-model="cond.role_id" placeholder="角色" style="width: 100%;">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-col>
                    <el-col :span="6"><el-input-number v-model="cond.affection_value" placeholder="数值要求" style="width: 100%;" /></el-col>
                  </template>
                  <template v-else-if="cond.type === '道具'">
                    <el-col :span="12"><el-input v-model="cond.item_name" placeholder="道具名称" style="width: 100%;" /></el-col>
                  </template>
                  <template v-else-if="cond.type === '服装'">
                    <el-col :span="6">
                      <el-select v-model="cond.costume_role_id" placeholder="角色" style="width: 100%;" @change="onCostumeRoleChange(cond)">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <el-select v-model="cond.costume_name" placeholder="服装名称" style="width: 100%;" multiple>
                        <el-option v-for="costume in getCostumesForRole(cond.costume_role_id)" :key="costume.id" :label="costume.name" :value="costume.name" />
                      </el-select>
                    </el-col>
                  </template>
                  <el-col :span="2">
                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="editNode.entry_conditions.splice(cIdx, 1)" />
                  </el-col>
                </el-row>
              </div>
              <el-button type="warning" plain size="mini" @click="addEntryCondition">添加进入条件</el-button>
            </el-form-item>

            <!-- 是否提示隐藏UI -->
            <el-form-item>
              <el-checkbox v-model="editNode.hidden_ui">是否提示隐藏UI</el-checkbox>
            </el-form-item>

            <!-- 场景角色、开场白角色、回合数限制 -->
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="场景角色">
                  <el-select v-model="editNode.role_ids" multiple placeholder="选择角色" style="width: 100%;">
                    <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="开场白角色">
                  <el-select v-model="editNode.prologue_role_id" placeholder="选择角色" style="width: 100%;">
                    <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="回合数限制">
                  <el-input-number v-model="editNode.limit_turn" :min="0" placeholder="输入回合数限制" style="width: 100%;" />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 开场白内容和攻略方式 -->
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="开场白内容">
                  <el-input type="textarea" v-model="editNode.prologue" placeholder="输入开场白内容" :autosize="{ minRows: 3, maxRows: 6 }" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="攻略方式">
                  <el-input type="textarea" v-model="editNode.node_task" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入攻略方式..." />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- 进度指标管理 -->
            <el-divider content-position="center"><span style="color: #409EFF; font-weight: 600;">进度指标管理</span></el-divider>
            <el-form-item label="进度指标列表">
              <div v-for="(proc, pIdx) in editNode.process" :key="pIdx" style="border: 1px solid #409EFF; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9ff;">
                <!-- 紧凑模式 -->
                <div v-if="!proc._editing" style="display: flex; align-items: center; justify-content: space-between;">
                  <span style="flex: 1; color: #409EFF; font-size: 13px;">进度指标{{pIdx + 1}}: {{ formatProcessSummary(proc) }}</span>
                  <div>
                    <el-button type="primary" size="mini" @click="$set(proc, '_editing', true)">编辑</el-button>
                    <el-button type="danger" size="mini" @click="removeProcess(pIdx)">删除</el-button>
                  </div>
                </div>
                <!-- 展开模式 -->
                <template v-else>
                  <div style="display: flex; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: bold; margin-right: 10px; color: #409EFF;">进度指标 {{pIdx + 1}}</span>
                    <el-button size="mini" @click="$set(proc, '_editing', false)">收起</el-button>
                    <el-button type="danger" size="mini" @click="removeProcess(pIdx)">删除</el-button>
                  </div>

                  <el-row :gutter="20">
                    <el-col :span="6">
                      <el-form-item label="指标名称">
                        <el-input v-model="proc.name" placeholder="如：愤怒值" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="6">
                      <el-form-item label="起始值">
                        <el-input-number v-model="proc.start_process" :min="0" style="width: 100%;" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="6">
                      <el-form-item label="目标值">
                        <el-input-number v-model="proc.end_process" :min="0" style="width: 100%;" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="6">
                      <el-form-item label="绑定结局">
                        <el-select v-model="proc.ending_name" placeholder="选择结局（可选）" clearable style="width: 100%;">
                          <el-option v-for="name in endingList" :key="name" :label="name" :value="name" />
                        </el-select>
                      </el-form-item>
                    </el-col>
                  </el-row>

                  <!-- 奖励配置 -->
                  <el-form-item label="成就/道具/事件/好感度/属性">
                    <div v-for="(award, aIdx) in proc.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                      <el-row :gutter="10">
                        <el-col :span="6">
                          <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                            <el-option label="成就" value="成就" />
                            <el-option label="道具" value="道具" />
                            <el-option label="事件" value="事件" />
                            <el-option label="好感度" value="好感度" />
                            <el-option label="属性" value="属性" />
                          </el-select>
                        </el-col>
                        <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                          <el-col :span="16">
                            <el-input v-model="award.reward_description" placeholder="奖励描述" />
                          </el-col>
                        </template>
                        <template v-else-if="award.type === '好感度'">
                          <el-col :span="8">
                            <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                              <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                            </el-select>
                          </el-col>
                          <el-col :span="8">
                            <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                          </el-col>
                        </template>
                        <template v-else-if="award.type === '属性'">
                          <el-col :span="8">
                            <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                              <el-option v-for="attr in attributeOptions" :key="attr" :label="attr" :value="attr" />
                            </el-select>
                          </el-col>
                          <el-col :span="8">
                            <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                          </el-col>
                        </template>
                        <el-col :span="2">
                          <el-button type="danger" size="mini" icon="el-icon-delete" @click="proc.award_conf.splice(aIdx, 1)" />
                        </el-col>
                      </el-row>
                    </div>
                    <el-button type="primary" plain size="mini" @click="addAwardConf(proc)">添加奖励</el-button>
                  </el-form-item>
                </template>
              </div>
              <el-button type="success" plain @click="addProcess">添加新进度指标</el-button>
            </el-form-item>

            <!-- 用户消息处理 -->
            <el-divider content-position="center"><span style="font-weight: 600;">用户消息处理</span></el-divider>
            <el-form-item label="用户消息列表">
              <div v-if="editNode.user_messages_new && editNode.user_messages_new.length > 0" style="color: #409EFF; margin-bottom: 10px; font-size: 12px;">
                当前共 {{editNode.user_messages_new.length}} 条消息
              </div>
              <div v-for="(msg, msgIdx) in editNode.user_messages_new" :key="'msg-' + msgIdx" style="border: 1px solid #66b1ff; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #ecf5ff;">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                  <span style="font-weight: bold; margin-right: 10px;">消息 {{msgIdx + 1}}</span>
                  <el-button type="danger" size="mini" icon="el-icon-delete" @click="editNode.user_messages_new.splice(msgIdx, 1)">删除</el-button>
                </div>
                <el-form-item label="如果用户发的消息">
                  <el-input type="textarea" v-model="msg.result" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：用户提到了某个关键词..." />
                </el-form-item>
                <el-form-item label="则（AI回复方式）">
                  <el-input type="textarea" v-model="msg.ai_reply" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：AI应该如何回应..." />
                </el-form-item>
                <el-form-item label="进度指标名称">
                  <el-select v-model="msg.progress_name" placeholder="选择进度指标（可选）" clearable style="width: 100%;">
                    <el-option label="无" value="" />
                    <el-option v-for="proc in getProcessList()" :key="proc.name" :label="proc.name" :value="proc.name" />
                  </el-select>
                </el-form-item>
                <el-form-item label="实化数值" v-if="msg.progress_name">
                  <el-input-number v-model="msg.progress_value" placeholder="进度增减值" style="width: 100%;" />
                </el-form-item>
                <!-- 用户消息输出示例 -->
                <el-form-item label="输出示例">
                  <div v-for="(ex, exIdx) in msg.output_examples" :key="'umoe-' + exIdx" style="display: flex; align-items: center; margin-bottom: 4px;">
                    <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeUserMsgOutputExample(msg, exIdx)" />
                  </div>
                  <el-button type="primary" plain size="mini" @click="addUserMsgOutputExample(msg)">添加输出示例</el-button>
                </el-form-item>
              </div>
              <el-button type="primary" plain size="mini" @click="addUserMessage">添加用户消息处理</el-button>
            </el-form-item>

            <!-- 彩蛋管理 -->
            <el-divider content-position="center"><span style="color: #67C23A; font-weight: 600;">彩蛋管理</span></el-divider>
            <el-form-item label="彩蛋列表">
              <div v-for="(egg, eIdx) in editNode.egg" :key="eIdx" style="border: 1px solid #67C23A; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9f0;">
                <!-- 紧凑模式 -->
                <div v-if="!egg._editing" style="display: flex; align-items: center; justify-content: space-between;">
                  <span style="flex: 1; color: #67C23A; font-size: 13px;">彩蛋{{eIdx + 1}}: {{ formatEggSummary(egg) }}</span>
                  <div>
                    <el-button type="primary" size="mini" @click="$set(egg, '_editing', true)">编辑</el-button>
                    <el-button type="danger" size="mini" @click="editNode.egg.splice(eIdx, 1)">删除</el-button>
                  </div>
                </div>
                <!-- 展开模式 -->
                <template v-else>
                  <div style="display: flex; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: bold; margin-right: 10px; color: #67C23A;">彩蛋 {{eIdx + 1}}</span>
                    <el-button size="mini" @click="$set(egg, '_editing', false)">收起</el-button>
                    <el-button type="danger" size="mini" @click="editNode.egg.splice(eIdx, 1)">删除</el-button>
                  </div>

                  <el-form-item label="彩蛋名称">
                    <el-input v-model="egg.egg_name" placeholder="请输入彩蛋名称" />
                  </el-form-item>

                  <el-form-item label="触发条件">
                    <el-input type="textarea" v-model="egg.trigger_condition" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入触发条件描述..." />
                  </el-form-item>

                  <!-- 进度增益 -->
                  <el-form-item label="进度增益">
                    <div v-if="!editNode.process || !Array.isArray(editNode.process) || editNode.process.length === 0" style="color: #999; margin-bottom: 10px;">
                      请先添加进度指标，才能配置进度增益
                    </div>
                    <div v-for="(pg, pgIdx) in egg.process" :key="pgIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                      <el-row :gutter="10">
                        <el-col :span="10">
                          <el-select v-model="pg.process_name" placeholder="选择进度指标" style="width: 100%;">
                            <el-option v-for="proc in getProcessList()" :key="proc.name" :label="proc.name" :value="proc.name" />
                          </el-select>
                        </el-col>
                        <el-col :span="12">
                          <el-input-number v-model="pg.process_gain" placeholder="增益值" style="width: 100%;" />
                        </el-col>
                        <el-col :span="2">
                          <el-button type="danger" size="mini" icon="el-icon-delete" @click="egg.process.splice(pgIdx, 1)" />
                        </el-col>
                      </el-row>
                    </div>
                    <el-button type="primary" plain size="mini" @click="addProcessGain(egg)" :disabled="!editNode.process || !Array.isArray(editNode.process) || editNode.process.length === 0">添加进度增益</el-button>
                  </el-form-item>

                  <!-- 彩蛋奖励配置 -->
                  <el-form-item label="成就/道具/事件/好感度/属性">
                    <div v-for="(award, aIdx) in egg.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                      <el-row :gutter="10">
                        <el-col :span="6">
                          <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                            <el-option label="成就" value="成就" />
                            <el-option label="道具" value="道具" />
                            <el-option label="事件" value="事件" />
                            <el-option label="好感度" value="好感度" />
                            <el-option label="属性" value="属性" />
                          </el-select>
                        </el-col>
                        <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                          <el-col :span="16">
                            <el-input v-model="award.reward_description" placeholder="奖励描述" />
                          </el-col>
                        </template>
                        <template v-else-if="award.type === '好感度'">
                          <el-col :span="8">
                            <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                              <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                            </el-select>
                          </el-col>
                          <el-col :span="8">
                            <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                          </el-col>
                        </template>
                        <template v-else-if="award.type === '属性'">
                          <el-col :span="8">
                            <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                              <el-option v-for="attr in attributeOptions" :key="attr" :label="attr" :value="attr" />
                            </el-select>
                          </el-col>
                          <el-col :span="8">
                            <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                          </el-col>
                        </template>
                        <el-col :span="2">
                          <el-button type="danger" size="mini" icon="el-icon-delete" @click="egg.award_conf.splice(aIdx, 1)" />
                        </el-col>
                      </el-row>
                    </div>
                    <el-button type="primary" plain size="mini" @click="addEggAwardConf(egg)">添加奖励</el-button>
                  </el-form-item>
                </template>
              </div>
              <el-button type="success" plain @click="addEgg">添加新彩蛋</el-button>
            </el-form-item>

            <!-- 输出示例 -->
            <el-divider content-position="center"><span style="font-weight: 600;">输出示例</span></el-divider>
            <el-form-item label="输出示例">
              <div v-for="(example, index) in editNode.output_examples" :key="index" style="border: 1px solid #ddd; padding: 15px; margin-bottom: 10px; border-radius: 4px;">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                  <span style="font-weight: bold; margin-right: 10px;">示例 {{index + 1}}</span>
                  <el-checkbox v-model="example.is_tile" style="margin-right: 10px;">是否只用于结尾</el-checkbox>
                  <el-button type="danger" size="mini" @click="editNode.output_examples.splice(index, 1)">删除</el-button>
                </div>
                <el-input type="textarea" v-model="example.content" :autosize="{ minRows: 5, maxRows: 8 }" placeholder="请输入输出示例..." />
              </div>
              <el-button type="primary" plain size="mini" @click="addOutputExample">添加示例</el-button>
            </el-form-item>
          </el-form>
        </div>
      </template>
    </div>

    <span slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button v-if="!isCreate" type="danger" @click="handleDelete">删除</el-button>
      <el-button type="primary" @click="handleSave">保存</el-button>
    </span>
  </el-dialog>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { GameNode, EntryCondition, Chapter, PlotEnding, autoCompleteNode } from '@/api/node'
import { Role } from '@/api/role'
import { Costume, getCostumeList } from '@/api/costume'
import { GameAttribute } from '@/api/gameAttribute'
import { getToken } from '@/utils/auth'

@Component
export default class NodeEditDialog extends Vue {
  @Prop({ default: false }) readonly visible!: boolean
  @Prop({ default: null }) readonly node!: GameNode | null
  @Prop({ default: () => [] }) readonly allNodes!: GameNode[]
  @Prop({ default: () => [] }) readonly roles!: Role[]
  @Prop({ default: () => [] }) readonly chapters!: Chapter[]
  @Prop({ default: 0 }) readonly gameId!: number
  @Prop({ default: () => ({}) }) readonly initialConnections!: Record<number, number>

  editNode: any = this.createEmptyNode()
  processTargets: Record<number, number | undefined> = {}
  uploading = false
  costumesCache: Record<number, Costume[]> = {}
  uploadUrl = '/api_v2/upload'

  @Prop({ default: () => ['智慧值', '灵敏值', '精神值', '圣母值', '黑化值'] }) readonly attributeOptions!: string[]
  @Prop({ default: () => [] }) readonly gameAttributes!: GameAttribute[]

  // 进入条件-属性：角色选项（有按角色挂载的属性配置时只列有属性的角色；否则回退全部角色）
  get condAttrRoles(): Role[] {
    if (this.gameAttributes.length > 0) {
      const roleIds = [...new Set(this.gameAttributes.map(a => a.role_id))]
      return roleIds.map(id => this.roles.find(r => r.id === id)).filter(Boolean) as Role[]
    }
    return this.roles
  }

  // 进入条件-属性：属性名选项（按所选角色过滤；无属性配置时回退旧的全局属性列表）
  condAttrNames(cond: any): string[] {
    if (this.gameAttributes.length > 0) {
      if (!cond.role_id) return []
      return this.gameAttributes.filter(a => a.role_id === cond.role_id).map(a => a.name)
    }
    return this.attributeOptions
  }

  get dialogVisible() {
    return this.visible
  }

  set dialogVisible(val: boolean) {
    this.$emit('update:visible', val)
  }

  get isCreate() {
    return !this.node || !this.node.id
  }

  get otherNodes() {
    if (!this.editNode.id) return this.allNodes
    return this.allNodes.filter(n => n.id !== this.editNode.id)
  }

  get uploadHeaders() {
    const token = getToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  /** plot_end 数组（兼容旧的 string 格式） */
  get plotEndArray(): PlotEnding[] {
    if (!this.editNode.current_plot_new) return []
    const pe = this.editNode.current_plot_new.plot_end
    if (Array.isArray(pe)) return pe
    return []
  }

  /** 结局名称列表 */
  get endingList(): string[] {
    return this.plotEndArray
      .map((e: PlotEnding) => e.name)
      .filter((n: string) => n && n.trim() !== '')
  }

  /** 是否可以自动补全 */
  get canAutoComplete(): boolean {
    if (!this.editNode.current_plot_new) return false
    const plot = this.editNode.current_plot_new
    const hasPlotStart = plot.plot_start && plot.plot_start.trim() !== ''
    const hasPlotProcess = plot.plot_process && plot.plot_process.trim() !== ''
    const hasPlotEnd = Array.isArray(plot.plot_end) ? plot.plot_end.length > 0 : (typeof plot.plot_end === 'string' && plot.plot_end.trim() !== '')
    const hasPrologue = this.editNode.prologue && this.editNode.prologue.trim() !== ''
    const hasNodeTask = this.editNode.node_task && this.editNode.node_task.trim() !== ''
    return !!(hasPlotStart && hasPlotProcess && hasPlotEnd && hasPrologue && hasNodeTask)
  }

  @Watch('visible')
  onVisibleChange(val: boolean) {
    if (val) {
      if (this.node && this.node.id) {
        this.editNode = JSON.parse(JSON.stringify(this.node))
        this.initNodeFields()
        // AI 节点：从 endings 加载连线 targets；固定节点：使用 initialConnections
        if (this.editNode.node_type === 'ai') {
          const targets: Record<number, number | undefined> = {}
          const endings = this.plotEndArray
          endings.forEach((ending: PlotEnding, idx: number) => {
            if (ending.next_node_id != null) {
              targets[idx] = ending.next_node_id
            }
          })
          this.processTargets = targets
        } else {
          this.processTargets = { ...this.initialConnections }
        }
        this.preloadCostumesForEntryConditions()
      } else {
        this.editNode = this.createEmptyNode()
        this.processTargets = {}
      }
    }
  }

  createEmptyNode(): GameNode {
    return {
      title: '',
      description: '',
      role_ids: [],
      prologue: '',
      dialogues: [],
      node_type: 'ai',
      game_id: this.gameId,
      bg_url: '',
      music_url: '',
      game_chapter_id: undefined,
      story_background: '',
      hidden_ui: false,
      limit_turn: undefined,
      node_task: '',
      image_prompt: '',
      user_messages_new: [],
      process: [],
      egg: [],
      output_examples: [],
      dialogue_text: '',
      entry_conditions: [],
      current_plot_new: {
        plot_start: '',
        plot_process: '',
        plot_end: [] as PlotEnding[]
      }
    }
  }

  initNodeFields() {
    const node = this.editNode
    if (node.node_type === 'ai') {
      if (!node.user_special) this.$set(node, 'user_special', [])
      if (!node.story_background) this.$set(node, 'story_background', '')
      if (node.hidden_ui === undefined) this.$set(node, 'hidden_ui', false)

      // 校验并修正 current_plot_new 格式
      const isValidPlotNew = node.current_plot_new
        && typeof node.current_plot_new === 'object'
        && 'plot_start' in node.current_plot_new
        && 'plot_process' in node.current_plot_new
        && 'plot_end' in node.current_plot_new

      if (!isValidPlotNew) {
        this.$set(node, 'current_plot_new', { plot_start: '', plot_process: '', plot_end: [] })
      } else {
        // plot_end 向后兼容：旧数据 plot_end 是 string → 转为数组
        const plotNew = node.current_plot_new!
        if (typeof plotNew.plot_end === 'string') {
          const old = plotNew.plot_end
          plotNew.plot_end = old.trim()
            ? [{ name: '结局1', content: old, output_examples: [] }]
            : []
        }
      }

      if (!node.image_prompt) this.$set(node, 'image_prompt', '')
      if (!node.process) this.$set(node, 'process', [])
      if (!node.egg) this.$set(node, 'egg', [])
      if (!node.user_messages_new) this.$set(node, 'user_messages_new', [])
      // user_messages_new 兼容处理
      if (node.user_messages_new && Array.isArray(node.user_messages_new)) {
        node.user_messages_new.forEach((msg: any) => {
          msg.progress_name = msg.progress_name || ''
          if (!msg.output_examples) msg.output_examples = []
          delete msg.egg_name
        })
      }
      if (!node.output_examples) this.$set(node, 'output_examples', [])
    }
    if (!node.entry_conditions) this.$set(node, 'entry_conditions', [])

    // 固定节点：将 dialogues 数组转换为 dialogue_text 文本
    if (node.node_type === 'fixed' && node.dialogues && Array.isArray(node.dialogues) && !node.dialogue_text) {
      this.$set(node, 'dialogue_text', this.dialoguesToText(node.dialogues))
    }
  }

  // ========== 结局管理 ==========
  addEnding() {
    if (!this.editNode.current_plot_new) return
    if (!Array.isArray(this.editNode.current_plot_new.plot_end)) {
      this.$set(this.editNode.current_plot_new, 'plot_end', [])
    }
    const endings = this.editNode.current_plot_new.plot_end as PlotEnding[]
    endings.push({
      name: `结局${endings.length + 1}`,
      content: '',
      output_examples: [],
      next_node_id: null
    })
  }

  removeEnding(index: number) {
    if (!this.editNode.current_plot_new || !Array.isArray(this.editNode.current_plot_new.plot_end)) return
    (this.editNode.current_plot_new.plot_end as PlotEnding[]).splice(index, 1)
  }

  addEndingOutputExample(ending: PlotEnding) {
    if (!ending.output_examples) {
      ending.output_examples = []
    }
    ending.output_examples.push({ content: '' })
  }

  removeEndingOutputExample(ending: PlotEnding, index: number) {
    if (ending.output_examples) {
      ending.output_examples.splice(index, 1)
    }
  }

  // ========== 进度指标 ==========
  formatProcessSummary(proc: any): string {
    let parts: string[] = []
    parts.push(`${proc.name || '未命名'} (${proc.start_process ?? 0}~${proc.end_process ?? 100})`)
    if (proc.award_conf && proc.award_conf.length > 0) {
      proc.award_conf.forEach((a: any) => {
        if (a.type === '事件') parts.push(`事件【${a.reward_description || ''}】`)
        else if (a.type === '属性') parts.push(`属性【${a.attribute_name || ''}${a.attribute_value > 0 ? '+' : ''}${a.attribute_value || 0}】`)
        else parts.push(`${a.type}【${a.reward_description || ''}】`)
      })
    }
    if (proc.ending_name) parts.push(`进入${proc.ending_name}`)
    return parts.join(', ')
  }

  addProcess() {
    if (!this.editNode.process) this.$set(this.editNode, 'process', [])
    this.editNode.process.push({
      name: '',
      start_process: 0,
      end_process: 100,
      award_conf: [],
      points_reward: [],
      affection_reward: [],
      _editing: true
    })
  }

  removeProcess(index: number) {
    const processName = this.editNode.process[index]?.name
    this.editNode.process.splice(index, 1)
    delete this.processTargets[index]
    // 重新映射 processTargets 的索引
    const newTargets: Record<number, number | undefined> = {}
    Object.keys(this.processTargets).forEach(key => {
      const k = Number(key)
      if (k > index) {
        newTargets[k - 1] = this.processTargets[k]
      } else {
        newTargets[k] = this.processTargets[k]
      }
    })
    this.processTargets = newTargets
    // 清理引用
    if (processName && processName.trim() !== '') {
      if (this.editNode.user_messages_new) {
        this.editNode.user_messages_new.forEach((msg: any) => {
          if (msg.progress_name === processName) {
            msg.progress_name = ''
            msg.progress_value = 0
          }
        })
      }
      if (this.editNode.egg) {
        this.editNode.egg.forEach((egg: any) => {
          if (egg.process) {
            egg.process = egg.process.filter((pg: any) => pg.process_name !== processName)
          }
        })
      }
    }
  }

  addAwardConf(proc: any) {
    if (!proc.award_conf) this.$set(proc, 'award_conf', [])
    proc.award_conf.push({ type: '成就', reward_description: '' })
  }

  // ========== 彩蛋 ==========
  formatEggSummary(egg: any): string {
    let parts: string[] = []
    if (egg.trigger_condition) {
      const tc = egg.trigger_condition.length > 20 ? egg.trigger_condition.substring(0, 20) + '...' : egg.trigger_condition
      parts.push(`触发条件: ${tc}`)
    }
    if (egg.egg_name) parts.push(`触发彩蛋【${egg.egg_name}】`)
    if (egg.award_conf && egg.award_conf.length > 0) {
      egg.award_conf.forEach((a: any) => {
        if (a.type === '成就') parts.push(`奖励成就【${a.reward_description || ''}】`)
        else parts.push(`${a.type}【${a.reward_description || ''}】`)
      })
    }
    return parts.join(', ') || '空彩蛋'
  }

  addEgg() {
    if (!this.editNode.egg) this.$set(this.editNode, 'egg', [])
    this.editNode.egg.push({
      egg_name: '',
      trigger_condition: '',
      process: [],
      award_conf: [],
      points_reward: [],
      affection_reward: [],
      _editing: true
    })
  }

  addProcessGain(egg: any) {
    if (!egg.process) this.$set(egg, 'process', [])
    const validProcesses = this.getProcessList()
    const defaultProcessName = validProcesses.length > 0 ? validProcesses[0].name : ''
    egg.process.push({ process_name: defaultProcessName, process_gain: 0 })
  }

  addEggAwardConf(egg: any) {
    if (!egg.award_conf) this.$set(egg, 'award_conf', [])
    egg.award_conf.push({ type: '成就', reward_description: '' })
  }

  // ========== 输出示例 ==========
  addOutputExample() {
    if (!this.editNode.output_examples) this.$set(this.editNode, 'output_examples', [])
    this.editNode.output_examples.push({ content: '' })
  }

  // ========== 用户消息 ==========
  addUserMessage() {
    if (!this.editNode.user_messages_new) this.$set(this.editNode, 'user_messages_new', [])
    this.editNode.user_messages_new.push({
      result: '',
      ai_reply: '',
      progress_name: '',
      progress_value: 0,
      output_examples: []
    })
  }

  addUserMsgOutputExample(msg: any) {
    if (!msg.output_examples) {
      msg.output_examples = []
    }
    msg.output_examples.push({ content: '' })
  }

  removeUserMsgOutputExample(msg: any, index: number) {
    if (msg.output_examples) {
      msg.output_examples.splice(index, 1)
    }
  }

  // ========== 进入条件 ==========
  addEntryCondition() {
    if (!this.editNode.entry_conditions) this.$set(this.editNode, 'entry_conditions', [])
    this.editNode.entry_conditions.push({
      type: '货币',
      value: 0,
      is_free: false,
      attribute_name: '',
      attribute_value: 0,
      role_id: undefined,
      affection_value: 0,
      item_name: '',
      costume_role_id: undefined,
      costume_name: []
    })
  }

  // ========== 工具方法 ==========
  getProcessList(): any[] {
    if (!this.editNode.process || !Array.isArray(this.editNode.process)) return []
    return this.editNode.process.filter((p: any) => p.name && p.name.trim() !== '')
  }

  getCostumesForRole(roleId: number | undefined): Costume[] {
    if (!roleId) return []
    return this.costumesCache[roleId] || []
  }

  async onCostumeRoleChange(cond: EntryCondition) {
    cond.costume_name = []
    if (!cond.costume_role_id) return
    if (!this.costumesCache[cond.costume_role_id]) {
      try {
        const costumes = await getCostumeList(this.gameId, cond.costume_role_id)
        this.$set(this.costumesCache, cond.costume_role_id, costumes)
      } catch (error) {
        console.error('加载服装列表失败:', error)
      }
    }
  }

  async preloadCostumesForEntryConditions() {
    if (!this.editNode.entry_conditions || !Array.isArray(this.editNode.entry_conditions)) return
    const roleIds = new Set<number>()
    for (const cond of this.editNode.entry_conditions) {
      if (cond.type === '服装' && cond.costume_role_id) {
        roleIds.add(cond.costume_role_id)
      }
    }
    const loadPromises = Array.from(roleIds).map(async (roleId) => {
      if (!this.costumesCache[roleId]) {
        try {
          const costumes = await getCostumeList(this.gameId, roleId)
          this.$set(this.costumesCache, roleId, costumes)
        } catch (error) {
          console.error(`加载角色${roleId}的服装列表失败:`, error)
        }
      }
    })
    await Promise.all(loadPromises)
  }

  getFileName(url: string): string {
    if (!url) return ''
    try {
      const u = new URL(url, window.location.origin)
      const path = u.pathname
      const base = path.substring(path.lastIndexOf('/') + 1)
      return decodeURIComponent(base) || url
    } catch {
      return url
    }
  }

  handleUploadSuccess(res: any, field: 'bg_url' | 'music_url') {
    this.uploading = false
    if (res && res.url) {
      this.$set(this.editNode, field, res.url)
      if (field === 'bg_url' && res.id) {
        this.$set(this.editNode, 'bg_resource_id', res.id)
      } else if (field === 'music_url' && res.id) {
        this.$set(this.editNode, 'music_resource_id', res.id)
      }
    }
  }

  // ========== 自动补全 ==========
  async doAutoComplete() {
    if (!this.canAutoComplete) return
    this.$set(this.editNode, '_autoCompleting', true)
    try {
      const result = await autoCompleteNode({
        node_id: this.editNode.id,
        game_id: this.gameId,
        plot_start: this.editNode.current_plot_new!.plot_start,
        plot_process: this.editNode.current_plot_new!.plot_process,
        plot_end: this.editNode.current_plot_new!.plot_end,
        prologue: this.editNode.prologue,
        node_task: this.editNode.node_task || ''
      })
      if (result.story_background) this.$set(this.editNode, 'story_background', result.story_background)
      if (result.role_ids && result.role_ids.length > 0) this.$set(this.editNode, 'role_ids', result.role_ids)
      if (result.limit_turn !== null && result.limit_turn !== undefined) this.$set(this.editNode, 'limit_turn', result.limit_turn)
      if (result.process && result.process.length > 0) this.$set(this.editNode, 'process', result.process)
      if (result.egg && result.egg.length > 0) this.$set(this.editNode, 'egg', result.egg)
      if (result.user_messages_new && result.user_messages_new.length > 0) this.$set(this.editNode, 'user_messages_new', result.user_messages_new)
      this.$message.success('补全完成')
    } catch (error: any) {
      console.error('自动补全失败:', error)
      this.$message.error('补全失败: ' + (error.response?.data?.message || error.message))
    } finally {
      this.$set(this.editNode, '_autoCompleting', false)
    }
  }

  // ========== 保存/删除 ==========
  handleSave() {
    // AI 节点：从 endings 的 next_node_id 构建 processTargets
    if (this.editNode.node_type === 'ai') {
      const endingTargets: Record<number, number | undefined> = {}
      const endings = this.plotEndArray
      endings.forEach((ending: PlotEnding, idx: number) => {
        if (ending.next_node_id != null) {
          endingTargets[idx] = ending.next_node_id
        }
      })
      this.$emit('save', this.editNode, endingTargets)
    } else {
      this.$emit('save', this.editNode, this.processTargets)
    }
    this.dialogVisible = false
  }

  handleDelete() {
    this.$confirm('确定删除该节点吗？', '提示', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      this.$emit('delete', this.editNode.id)
      this.dialogVisible = false
    }).catch(() => { /* cancelled */ })
  }

  handleClose() {
    this.$emit('update:visible', false)
  }

  // 将dialogues数组转换为文本格式（用于编辑）
  dialoguesToText(dialogues: any[]): string {
    if (!dialogues || !Array.isArray(dialogues) || dialogues.length === 0) {
      return ''
    }
    const lines = dialogues.map((d: any) => {
      const role = this.roles.find(r => r.id === d.role_id)
      const roleName = role ? role.name : '未知角色'
      return `${roleName}:${d.text || ''}`
    })
    return lines.join('\n')
  }
}
</script>

<style scoped lang="scss">
.node-edit-scroll {
  max-height: 75vh;
  overflow-y: auto;
  padding-right: 12px;
}

/* ========== 固定节点布局 ========== */
.fixed-node-layout {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.fixed-node-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fixed-node-left .el-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fixed-node-textarea ::v-deep .el-textarea__inner {
  min-height: 200px;
}

.fixed-node-right {
  width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
}

/* ========== AI节点布局 ========== */
.ai-node-layout {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.ai-node-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.ai-node-left .el-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ai-node-right {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
}

.ai-node-settings {
  padding: 0 0 0 0;
}

/* ========== 当前剧情 ========== */
.current-plot-item {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.current-plot-item ::v-deep .el-form-item__content {
  flex: 1;
  display: flex;
}

.current-plot-container {
  flex: 1;
  border: 1px solid #e5e7eb;
  padding: 15px;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.plot-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.plot-row:last-child {
  margin-bottom: 0;
}

.plot-label {
  display: inline-block;
  width: 80px;
  font-size: 13px;
  color: #606266;
  line-height: 32px;
  flex-shrink: 0;
}

.plot-textarea {
  flex: 1;
}

.plot-textarea ::v-deep .el-textarea__inner {
  resize: vertical;
  min-height: auto !important;
}

/* ========== 媒体资源 ========== */
.media-section.bg-section {
  flex-shrink: 0;
}

.bg-upload-wrapper {
  width: 100%;
}

.bg-upload-wrapper ::v-deep .el-upload {
  width: 100%;
  display: block;
}

.bg-preview-container {
  width: 100%;
}

.bg-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 2;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.bg-preview:hover {
  border-color: #6366f1;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.bg-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 14px;
}

.bg-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
}

.bg-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
}

.bg-preview:hover .bg-overlay {
  opacity: 1;
}

.bg-overlay i {
  font-size: 28px;
  margin-bottom: 8px;
}

.overlay-text {
  font-size: 13px;
  font-weight: 500;
}

.bg-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.bg-filename:hover {
  background: rgba(0, 0, 0, 0.7);
  border-color: #6366f1;
}

.bg-filename:hover .label,
.bg-filename:hover .filename {
  color: white;
}

.bg-filename .label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.3s ease;
}

.bg-filename .filename {
  font-size: 12px;
  color: #374151;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
}

/* 音乐区域 */
.media-section.music-section {
  flex-shrink: 0;
}

.music-player {
  margin-bottom: 8px;
}

.music-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 14px;
}

.music-placeholder i {
  font-size: 20px;
}

.music-upload-wrapper {
  width: 100%;
}

.music-upload-wrapper ::v-deep .el-upload {
  width: 100%;
  display: block;
}

.music-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.music-filename:hover {
  background: rgba(0, 0, 0, 0.7);
  border-color: #6366f1;
}

.music-filename:hover .label,
.music-filename:hover .filename {
  color: white;
}

.music-filename:hover .music-overlay {
  opacity: 1;
}

.music-filename .label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.3s ease;
}

.music-filename .filename {
  font-size: 12px;
  color: #374151;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
}

.music-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
}

.music-overlay i {
  font-size: 16px;
}
</style>
